import { useAuth0 } from '@auth0/auth0-react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Table, Skeleton, Tag, Flex, Input, Button, Dropdown, theme, GetProp, TableProps, message } from 'antd';
import { css } from '@emotion/css';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { ArrowLongRightIcon } from '@heroicons/react/24/outline';
import { useBackoffice } from '../backoffice/useBackoffice';
import { EllipsisOutlined, CheckOutlined, CopyOutlined, LogoutOutlined } from '@ant-design/icons';
import { useCompleteTransfer } from './useCompleteTransfer';
import { TransferDetails } from './TransferDetails';
import { InitiateTransferWithdrawalStageModal } from './InitiateTransferWithdrawalStageModal';
import { NewTransferModal } from './NewTransferModal';
import Big from 'big.js';

const { Column } = Table;
const { Search } = Input;

export type TransferRow = {
  id: string;
  created_at: string;
  customer_beneficiary_type: 'individual' | 'corporate';
  customer_company_name: string | null;
  customer_id: string;
  customer_email: string;
  requested_amount: string;
  source_currency: string;
  source_network: string;
  destination_currency: string;
  destination_network: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  status: 'refunded' | 'pending' | 'successfully_processed' | 'processing';
  type: 'offramp' | 'onramp' | 'cross_chain';
};

type TablePaginationConfig = Exclude<GetProp<TableProps, 'pagination'>, boolean>;

interface TableParams {
  searchString?: string;
  pagination?: TablePaginationConfig;
}

const TransfersPage = () => {
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const { developerId, isBackofficeOn } = useBackoffice();
  const { token } = theme.useToken();
  const { getAccessTokenSilently } = useAuth0();
  const [newTransferModalOpened, setNewTransferModalOpened] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<TransferRow | null>(null);
  const [transferToWithdraw, setTransferToWithdraw] = useState<TransferRow | null>(null);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const { isCompleting, completeTransfer } = useCompleteTransfer();

  const transfersQuery = useQuery({
    queryKey: [
      'transfers',
      tableParams.pagination?.current,
      tableParams.pagination?.pageSize,
      tableParams.searchString,
      developerId,
    ],
    queryFn: async () => {
      const token = await getAccessTokenSilently();

      const res = await axios.get(`${process.env.REACT_APP_BACKEND_API_URL}/customers/transfers`, {
        params: {
          consumerId: developerId,
          search_string: tableParams.searchString,
          limit: tableParams.pagination?.pageSize,
          offset: tableParams.pagination?.pageSize! * (tableParams.pagination?.current! - 1),
        },

        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return res.data?.data;
    },
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  useEffect(() => {
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total: transfersQuery?.data?.totalCount,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfersQuery?.data?.totalCount]);

  const handleTableChange: TableProps<TransferRow>['onChange'] = (pagination) => {
    setTableParams({
      pagination,
    });
  };

  const handleCompleteTransfer = (record: TransferRow) => {
    completeTransfer({
      transferId: record.id,
      transferType: record.type,
    })
      .then(() => {
        messageApi.success('Successfully completed transfer!');
        return queryClient.invalidateQueries({
          queryKey: ['transfers'],
        });
      })
      .catch(() => {
        messageApi.error('Something is wrong');
      });
  };

  const handleTransferDetailsClose = () => {
    setSelectedTransfer(null);
  };

  const handleOpenTransferModal = () => {
    setNewTransferModalOpened(true)
  }

  const onSearch = (val: string) => {
    setTableParams({
      ...tableParams,
      searchString: val,
    });
  };

  if (transfersQuery.isLoading) {
    return <Skeleton active />;
  }

  return (
    <Flex gap="middle" vertical>
      {contextHolder}

      <Flex align="center" justify="space-between" gap="middle">
        <Search
          allowClear
          placeholder="Search by customer id, name or email..."
          onSearch={onSearch}
          style={{ width: 360 }}
        />

        <Button type="primary" onClick={handleOpenTransferModal}>
          Create Transfer
        </Button>
      </Flex>
      <Table<TransferRow>
        onChange={handleTableChange}
        pagination={tableParams.pagination}
        loading={transfersQuery.isFetching || isCompleting}
        dataSource={transfersQuery?.data?.items || []}
        onRow={(record) => ({
          onClick: (e) => {
            e.stopPropagation();
            setSelectedTransfer(record);
          },
          style: { cursor: 'pointer' },
        })}
      >
        <Column
          title="Date"
          key="date"
          render={(_: any, record: TransferRow) => {
            return dayjs(record.created_at).format('MMM D, YYYY');
          }}
        />
        <Column
          title="Customer"
          key="name"
          render={(_: any, record: TransferRow) => {
            let name = `${record.customer_first_name} ${record.customer_last_name}`;
            if (record.customer_company_name) {
              name = record.customer_company_name;
            }

            const subHeaderStyles = css`
              color: ${token.colorTextSecondary};
              font-size: 12px;
            `;
            return (
              <Flex vertical>
                <span>{name}</span>
                <span className={subHeaderStyles}>{record.customer_email}</span>
              </Flex>
            );
          }}
        />

        <Column
          title="Transfer ID"
          key="name"
          render={(_: any, record: TransferRow) => {
            const subHeaderStyles = css`
              color: ${token.colorTextSecondary};
            `;
            return (
              <Flex align="center">
                {record.id.substring(0, 12)}...
                <Button
                  type="text"
                  className={subHeaderStyles}
                  size="small"
                  icon={<CopyOutlined width={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(record.id);
                    messageApi.success('ID copied to clipboard');
                  }}
                />
              </Flex>
            );
          }}
        />

        <Column
          title="Amount"
          key="amount"
          render={(_: any, record: TransferRow) => {
            return `${new Big(record.requested_amount).toFixed(2).toLocaleString()} ${record.destination_currency.toUpperCase()}`;
          }}
        />

        <Column
          title="Route Details"
          key="routeDetails"
          render={(_: any, record: TransferRow) => {
            const iconStyles = css`
              width: 16px;
            `;
            const currencyPartStyles = css`
              color: ${token.colorTextSecondary};
              font-size: 12px;
            `;
            return (
              <Flex gap="small" align="center">
                <span>
                  {record.source_currency.toUpperCase()}{' '}
                  <span className={currencyPartStyles}>({record.source_network.toUpperCase()})</span>
                </span>
                <ArrowLongRightIcon className={iconStyles} />

                <span>
                  {record.destination_currency.toUpperCase()}{' '}
                  <span className={currencyPartStyles}>({record.destination_network.toUpperCase()})</span>
                </span>
              </Flex>
            );
          }}
        />

        <Column
          title="Type"
          key="type"
          render={(_: any, record: TransferRow) => {
            return record.type;
          }}
        />

        <Column
          title="Status"
          key="status"
          render={(_: any, record: TransferRow) => {
            let style = {
              backgroundColor: '#f7f8f9',
              color: '#333',
              border: '1px solid #eceef2',
              borderColor: '#eceef2',
              borderRadius: '6px',
            };
            let status = 'Awaiting Funds';

            if (record.status === 'successfully_processed') {
              style.color = '#067857';
              style.backgroundColor = '#d1fae4';
              style.borderColor = '#aaf5d2';
              status = 'Completed';
            } else if (record.status === 'processing') {
              status = 'Processing';
            } else if (record.status === 'refunded') {
              style.color = '#bb1f1d';
              style.backgroundColor = '#ffe4e3';
              style.borderColor = '#fdc8c9';
              status = 'Refunded';
            }

            return <Tag style={style}>{status}</Tag>;
          }}
        />

        {isBackofficeOn && (
          <Column
            title="Actions"
            key="actions"
            render={(_: any, record: TransferRow) => {
              const items = [];

              if (record.status !== 'successfully_processed') {
                items.push(
                  {
                    icon: <LogoutOutlined />,
                    label: 'Initiate Withdrawal Stage',
                    key: 'init_withdrawal_stage',
                  },
                  {
                    icon: <CheckOutlined />,
                    label: 'Complete Transfer',
                    key: 'mark_as_completed',
                  }
                );
              }

              return (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    onClick: (e) => {
                      e.domEvent.stopPropagation();
                      if (e.key === 'mark_as_completed') {
                        handleCompleteTransfer(record);
                      } else if (e.key === 'init_withdrawal_stage') {
                        setTransferToWithdraw(record);
                      }
                    },
                    items,
                  }}
                >
                  <Button type="default" onClick={(e) => e.stopPropagation()} icon={<EllipsisOutlined />} />
                </Dropdown>
              );
            }}
          />
        )}
      </Table>

      <TransferDetails transfer={selectedTransfer} onClose={handleTransferDetailsClose} />

      {transferToWithdraw && (
        <InitiateTransferWithdrawalStageModal
          transfer={transferToWithdraw}
          onClose={() => {
            setTransferToWithdraw(null);
          }}
        />
      )}

      {newTransferModalOpened && (
        <NewTransferModal
          onClose={() => {
            setNewTransferModalOpened(false);
          }}
          onTransferCreate={() => {
            messageApi.success('Transfer created successfully');
          }}
          onTransferCreateFail={() => {
            messageApi.error('Failed to create transfer');
          }}
        />
      )}
    </Flex>
  );
};

export default TransfersPage;
